/**
 * Адаптер API рабочего места «Тесты»: единственное место, которое знает адреса и формы запросов.
 * Экраны импортируют только отсюда; при переходе на ТЗ v2 меняется этот файл (спецификация, п. 9).
 */
import api from '../services/api';
import { getApiErrorCode, getApiErrorMessage } from '../utils/apiError';
import type { EditableQuestion } from '../components/QuestionInlineEditor';
import type {
  AssignmentRow,
  ComposeRequest,
  ComposeResult,
  CopyResult,
  Employee,
  LibraryList,
  OverviewDetail,
  OverviewItem,
  OverviewList,
  QuestionKind,
  TestBlock,
  TestStatus,
  UnansweredQuestions,
  AssignUnansweredResult,
} from './model';

export const errorCode = getApiErrorCode;
export const errorMessage = getApiErrorMessage;

/** Подробности структурированной ошибки `{code, message, …}`; для строки и списка — null. */
export function errorDetail(error: unknown): Record<string, unknown> | null {
  const detail = (error as { response?: { data?: { detail?: unknown } } } | null | undefined)
    ?.response?.data?.detail;
  return detail && typeof detail === 'object' && !Array.isArray(detail)
    ? (detail as Record<string, unknown>)
    : null;
}

const HUMAN_STATUSES = new Set([400, 403, 404, 409]);

/**
 * Текст ошибки для человека. Слова сервера показываем только для ответов 400/403/404/409:
 * там backend пишет готовую фразу по-русски. Для 5xx, 422 (проверка полей) и обрывов сети
 * («Network Error», «Request failed with status code 500») возвращается запасной текст экрана.
 */
export function humanError(error: unknown, fallback: string): string {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } } | null | undefined)
    ?.response;
  if (response?.status && HUMAN_STATUSES.has(response.status)) {
    const detail = response.data?.detail;
    const text = typeof detail === 'string'
      ? detail
      : (detail as { message?: unknown } | null | undefined)?.message;
    if (typeof text === 'string' && text.trim()) return text;
  }
  return fallback;
}

export interface OverviewQuery {
  status?: TestStatus;
  search?: string;
  sort?: 'updated' | 'created' | 'title';
  skip?: number;
  limit?: number;
}

export async function fetchOverview(query: OverviewQuery = {}): Promise<OverviewList> {
  const params: Record<string, string | number> = {};
  if (query.status) params.status = query.status;
  if (query.search && query.search.trim()) params.search = query.search.trim();
  if (query.sort) params.sort = query.sort;
  if (query.skip) params.skip = query.skip;
  params.limit = query.limit ?? 20;
  const { data } = await api.get<OverviewList>('/admin/test-overview', { params });
  return data;
}

export async function fetchOverviewDetail(testId: number): Promise<OverviewDetail> {
  const { data } = await api.get<OverviewDetail>(`/admin/test-overview/${testId}`);
  return data;
}

export interface LibraryQuery {
  forTestId?: number;
  search?: string;
  block?: TestBlock;
  subgroup?: string;
  type?: QuestionKind;
  skip?: number;
  limit?: number;
}

export async function fetchLibrary(query: LibraryQuery = {}): Promise<LibraryList> {
  const params: Record<string, string | number> = {};
  if (query.forTestId) params.for_test_id = query.forTestId;
  if (query.search && query.search.trim()) params.search = query.search.trim();
  if (query.block) params.block = query.block;
  if (query.subgroup) params.subgroup = query.subgroup;
  if (query.type) params.type = query.type;
  if (query.skip) params.skip = query.skip;
  params.limit = query.limit ?? 50;
  const { data } = await api.get<LibraryList>('/admin/questions/library', { params });
  return data;
}

export async function copyQuestions(
  testId: number,
  questionIds: number[],
  acknowledgeCompletedAttempts = false,
): Promise<CopyResult> {
  const { data } = await api.post<CopyResult>(`/admin/tests/${testId}/questions/copy`, {
    question_ids: questionIds,
    acknowledge_completed_attempts: acknowledgeCompletedAttempts,
  });
  return data;
}

export async function reorderQuestions(testId: number, questionIds: number[]): Promise<void> {
  await api.put(`/admin/tests/${testId}/questions/order`, { question_ids: questionIds });
}

export async function restoreQuestion(questionId: number): Promise<void> {
  await api.post(`/admin/questions/${questionId}/restore`);
}

export async function removeQuestion(questionId: number): Promise<void> {
  await api.delete(`/admin/questions/${questionId}`);
}

export async function deleteTest(testId: number): Promise<void> {
  await api.delete(`/admin/tests/${testId}`);
}

export async function archiveTest(testId: number): Promise<OverviewItem> {
  const { data } = await api.post<OverviewItem>(`/admin/tests/${testId}/archive`);
  return data;
}

export async function unarchiveTest(testId: number): Promise<OverviewItem> {
  const { data } = await api.post<OverviewItem>(`/admin/tests/${testId}/unarchive`);
  return data;
}

export async function publishTest(testId: number): Promise<void> {
  await api.post(`/admin/tests/${testId}/publish`);
}

export async function hideTest(testId: number): Promise<void> {
  await api.patch(`/admin/tests/${testId}`, { is_active: false });
}

export interface TestPatch {
  passing_score?: number;
  difficulty?: string | null;
  title?: string;
  description?: string;
  subgroup?: string;
  time_limit_minutes?: number;
}

export async function updateTest(testId: number, patch: TestPatch): Promise<void> {
  await api.patch(`/admin/tests/${testId}`, patch);
}

export async function duplicateTest(testId: number): Promise<{ id: number; title: string }> {
  try {
    const { data } = await api.post<{ id: number; title: string }>(`/admin/tests/${testId}/duplicate`);
    return data;
  } catch (error) {
    // При сбое копирования сервер отвечает 400 с текстом исключения («Ошибка дублирования теста: …»): людям он не нужен.
    const status = (error as { response?: { status?: number } } | null)?.response?.status;
    if (status === 400) throw new Error('duplicate_failed');
    throw error;
  }
}

export async function composeTest(body: ComposeRequest): Promise<ComposeResult> {
  const { data } = await api.post<ComposeResult>('/admin/tests/compose', body);
  return data;
}

export async function fetchSubgroups(): Promise<string[]> {
  const { data } = await api.get<string[]>('/admin/test-subgroups');
  return data;
}

interface UserRecord {
  id: number;
  full_name: string;
  role: string;
  is_active: boolean | null;
  position?: string | null;
  department?: string | null;
}

/** Сотрудники для выбора: только роль employee и активные, по алфавиту. */
export async function fetchEmployees(): Promise<Employee[]> {
  const { data } = await api.get<UserRecord[]>('/admin/users', { params: { limit: 1000 } });
  return data
    .filter((user) => user.role === 'employee' && user.is_active)
    .map((user) => ({
      id: user.id,
      full_name: user.full_name,
      position: user.position ?? null,
      department: user.department ?? null,
    }))
    .sort((left, right) => left.full_name.localeCompare(right.full_name, 'ru'));
}

/** Вопросы, на которые сотрудник ещё не отправлял ответ; настоящие копии сервер схлопывает. */
export async function fetchUnansweredQuestions(userId: number): Promise<UnansweredQuestions> {
  const { data } = await api.get<UnansweredQuestions>(`/admin/users/${userId}/unanswered-questions`);
  return data;
}

/** Создать персональный тест из выбранных непройденных вопросов и сразу выдать его сотруднику. */
export async function assignUnansweredQuestions(
  userId: number,
  questionIds: number[],
  title: string,
): Promise<AssignUnansweredResult> {
  const { data } = await api.post<AssignUnansweredResult>(
    `/admin/users/${userId}/unanswered-questions/assign`,
    { question_ids: questionIds, title },
  );
  return data;
}

export async function fetchAssignments(testId: number): Promise<AssignmentRow[]> {
  const { data } = await api.get<AssignmentRow[]>('/admin/test-assignments', { params: { test_id: testId } });
  return data;
}

export interface ExcludedAttempt {
  attempt_id: number;
  user_name: string;
  started_at: string | null;
  score: number | null;
  saved_answers: number;
  reason: string;
}

export async function fetchExcludedAttempts(testId: number): Promise<ExcludedAttempt[]> {
  const { data } = await api.get<{ items: ExcludedAttempt[] }>(`/admin/tests/${testId}/excluded-attempts`);
  return data.items;
}

export interface RestartOption {
  assignment_id: number;
  user_name: string;
  assigned_at: string;
  due_at: string | null;
  attempt_id: number;
  attempt_completed: boolean;
  score: number | null;
  is_passed: boolean | null;
}

export async function fetchRestartOptions(testId: number): Promise<RestartOption[]> {
  const { data } = await api.get<{ items: RestartOption[] }>(`/admin/tests/${testId}/restart-options`);
  return data.items;
}

export async function restartTest(testId: number, body: {
  assignment_id: number;
  expected_assigned_at: string;
  expected_attempt_id: number;
  expected_attempt_completed: boolean;
  due_at: string | null;
  reason: string;
}): Promise<{ user_name: string }> {
  const { data } = await api.post<{ user_name: string }>(`/admin/tests/${testId}/restart`, body);
  return data;
}

export interface AssignRequest {
  test_id: number;
  user_ids: number[];
  due_at?: string | null;
  allow_retake?: boolean;
  allow_similar_test?: boolean;
}

export async function assignTest(request: AssignRequest): Promise<void> {
  await api.post('/admin/test-assignments', {
    test_id: request.test_id,
    user_ids: request.user_ids,
    due_at: request.due_at ?? null,
    is_required: true,
    is_active: true,
    allow_retake: request.allow_retake ?? false,
    allow_similar_test: request.allow_similar_test ?? false,
  });
}

/**
 * Открыть или закрыть доступ по одной выдаче. При открытии сервер может ответить 409
 * `similar_test_confirmation_required`: тогда экран спрашивает и повторяет вызов с `allowSimilarTest = true`.
 */
export async function setAssignmentActive(
  assignmentId: number,
  isActive: boolean,
  allowSimilarTest = false,
): Promise<void> {
  await api.patch(
    `/admin/test-assignments/${assignmentId}`,
    allowSimilarTest ? { is_active: isActive, allow_similar_test: true } : { is_active: isActive },
  );
}

/** Вопросы теста со всеми вариантами ответа: нужны существующему редактору вопроса. */
export async function fetchTestQuestions(testId: number): Promise<EditableQuestion[]> {
  const { data } = await api.get<EditableQuestion[]>(`/admin/tests/${testId}/questions`);
  return data;
}
